import { getP } from 'redux-polyglot'
import api from '../libs/api'
import perf from '../libs/perf'

/**
 * Actions for initializing the application
 */
export const INIT_APP_REQUEST = 'DVR/INIT_APP_REQUEST'

const initAppRequest = () => ({
  type: INIT_APP_REQUEST,
})

export const initApp = () => dispatch => {
  return Promise.all([dispatch(initAppRequest()), dispatch(loadDvrs())])
}

/**
 * Actions for dismissing an error
 */
export const DISMISS_ERROR = 'DVR/DISMISS_ERROR'

export const dismissError = () => ({
  type: DISMISS_ERROR,
})

/**
 * Actions for fetching DVR list from API
 */
export const FETCH_DVRS_REQUEST = 'DVR/FETCH_DVRS_REQUEST'
export const FETCH_DVRS_SUCCESS = 'DVR/FETCH_DVRS_SUCCESS'
export const FETCH_DVRS_FAILURE = 'DVR/FETCH_DVRS_FAILURE'

const fetchDvrsRequest = () => ({
  type: FETCH_DVRS_REQUEST,
})

const fetchDvrsSuccess = payload => ({
  type: FETCH_DVRS_SUCCESS,
  payload,
})

const fetchDvrsFailure = error => ({
  type: FETCH_DVRS_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  error: true,
})

const sanitizeDvr = rawDvr => {
  const dvr = {
    id: rawDvr.event.id,
    name: rawDvr.event.name,
    desc: rawDvr.event.description,
    category: rawDvr.event.category,
    rating: {
      mpaa:
        typeof rawDvr.event.rating_mpaa === 'string' &&
        rawDvr.event.rating_mpaa.toLowerCase() !== 'none'
          ? rawDvr.event.rating_mpaa
          : '',
      tv:
        typeof rawDvr.event.rating_tv === 'string' &&
        rawDvr.event.rating_tv.toLowerCase() !== 'none'
          ? rawDvr.event.rating_tv
          : '',
    },
  }

  if (rawDvr.status === 'finished') {
    dvr.status = 'recorded'
  } else if (rawDvr.status === 'pending') {
    dvr.status = 'scheduled'
  } else if (rawDvr.status === 'recording') {
    dvr.status = 'recording'
  } else {
    dvr.status = 'failed'
  }

  if (dvr.status === 'recorded' && rawDvr.video) {
    if (rawDvr.video.sources && rawDvr.video.sources.hls) {
      dvr.playbackUrl = rawDvr.video.sources.hls
    }
    if (rawDvr.video.trick_play_url) {
      dvr.trickPlayUrl = rawDvr.video.trick_play_url
    }
  }

  if (rawDvr.video && rawDvr.video.img) {
    dvr.img = rawDvr.video.img.large
  } else if (rawDvr.event && rawDvr.event.img) {
    dvr.img = rawDvr.event.img.large
  }

  dvr.start = parseInt(rawDvr.start, 10)
  dvr.duration = parseInt(rawDvr.end - rawDvr.start, 10)
  dvr.lastUpdated = Date.now()

  if (rawDvr.channel) {
    dvr.channel = {
      id: rawDvr.channel.id,
      name: rawDvr.channel.name,
      img: rawDvr.channel.img.small,
    }
  }

  if (dvr.status === 'recorded') {
    try {
      dvr.currentTime = +global.localStorage.getItem(`dvr-curr-time-${dvr.id}`)
    } catch (e) {
      dvr.currentTime = 0
    }
  }

  return dvr
}

export const loadDvrs = () => (dispatch, getState) => {
  perf.start('dvr:list:fetch')
  dispatch(fetchDvrsRequest())
  return api
    .getDvrs()
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          const dvrs = {
            byId: {},
            allIds: [],
            filterList: [],
            filterLabelList: {},
          }
          response.results.forEach(dvr => {
            if (typeof dvr.event !== 'object') {
              return
            }
            const processed = sanitizeDvr(dvr)
            delete processed.playbackUrl
            dvrs.byId[processed.id] = processed
            dvrs.allIds.push(processed.id)
          })
          let categories =
            response.categories instanceof Array
              ? response.categories
              : typeof response.categories === 'object'
              ? Object.values(response.categories)
              : []
          if (categories.length > 0) {
            categories.sort().forEach(category => {
              const categId = category.toLowerCase().replace(' ', '')
              dvrs.filterList.push(categId)
              dvrs.filterLabelList[categId] = category
            })
          }
          perf.stop('dvr:list:fetch', 'retrieved the list of DVRs from server')
          return dispatch(fetchDvrsSuccess(dvrs))
        } else {
          return dispatch(fetchDvrsFailure(response.reason))
        }
      },
      () =>
        dispatch(
          fetchDvrsFailure(
            getP(getState()).t('tv_dvr.error_retrieving_dvr_list', {
              _: 'An error occurred while retrieving the list of DVRs',
            })
          )
        )
    )
}

/**
 * Actions for fetching a specific channel details from API
 */
export const FETCH_DVR_REQUEST = 'DVR/FETCH_DVR_REQUEST'
export const FETCH_DVR_SUCCESS = 'DVR/FETCH_DVR_SUCCESS'
export const FETCH_DVR_FAILURE = 'DVR/FETCH_DVR_FAILURE'
export const FETCH_DVR_WARNING = 'DVR/FETCH_DVR_WARNING'

const fetchDvrRequest = id => ({
  type: FETCH_DVR_REQUEST,
  payload: {
    id: id,
  },
})

const fetchDvrSuccess = payload => ({
  type: FETCH_DVR_SUCCESS,
  payload,
})

const fetchDvrFailure = (id, error) => ({
  type: FETCH_DVR_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

const fetchDvrWarning = (id, error) => ({
  type: FETCH_DVR_WARNING,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

export const loadDvr = (id, skipErrors = false) => (dispatch, getState) => {
  if (!skipErrors) {
    dispatch(fetchDvrRequest(id))
  }
  return api
    .getDvr(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(fetchDvrSuccess(sanitizeDvr(response.dvr)))
        } else {
          return dispatch(fetchDvrFailure(id, response.reason))
        }
      },
      err => {
        if (skipErrors) {
          return dispatch(fetchDvrWarning(id, err))
        } else {
          return dispatch(
            fetchDvrFailure(
              id,
              getP(getState()).t('tv_dvr.error_retrieving_dvr_details', {
                _: 'An error occurred while retrieving DVR details',
              })
            )
          )
        }
      }
    )
}

/**
 * Actions related to filters for recorded videos
 */
export const SET_FOCUSED_FILTER = 'DVR/LIST/SET_FOCUSED_FILTER'
export const SET_SELECTED_FILTER = 'DVR/LIST/SET_SELECTED_FILTER'
export const SET_FOCUSED_SECTION = 'DVR/LIST/SET_FOCUSED_SECTION'
export const SET_FOCUSED_POSITION = 'DVR/LIST/SET_FOCUSED_POSITION'

export const setFocusedFilter = focusedFilter => ({
  type: SET_FOCUSED_FILTER,
  payload: focusedFilter,
})
export const setSelectedFilter = selectedFilter => ({
  type: SET_SELECTED_FILTER,
  payload: selectedFilter,
})
export const setFocusedSection = focusedSection => ({
  type: SET_FOCUSED_SECTION,
  payload: focusedSection,
})
export const setFocusedPosition = (status, position) => ({
  type: SET_FOCUSED_POSITION,
  payload: {
    status,
    position,
  },
})

/**
 * Actions related to DVR playback
 */

export const UPDATE_CURRENT_TIME = 'DVR/PLAYBACK/UPDATE_CURRENT_TIME'

export const updateCurrentTime = (dvrId, currentTime) => {
  try {
    global.localStorage.setItem(`dvr-curr-time-${dvrId}`, currentTime)
  } catch (e) {
    // nothing to do
  }
  return {
    type: UPDATE_CURRENT_TIME,
    payload: {
      id: dvrId,
      currentTime,
    },
  }
}

/**
 * Actions for removing a DVR
 */
export const REMOVE_DVR_REQUEST = 'DVR/REMOVE_DVR_REQUEST'
export const REMOVE_DVR_SUCCESS = 'DVR/REMOVE_DVR_SUCCESS'
export const REMOVE_DVR_FAILURE = 'DVR/REMOVE_DVR_FAILURE'

const removeDvrRequest = id => ({
  type: REMOVE_DVR_REQUEST,
  payload: {
    id: id,
  },
})

const removeDvrSuccess = id => ({
  type: REMOVE_DVR_SUCCESS,
  payload: {
    id,
  },
})

const removeDvrFailure = (id, error) => ({
  type: REMOVE_DVR_FAILURE,
  payload: error instanceof Error ? error : new Error(error),
  meta: {
    id: id,
  },
  error: true,
})

export const removeDvr = id => (dispatch, getState) => {
  dispatch(removeDvrRequest(id))
  return api
    .removeDvr(id)
    .then(res => res.json())
    .then(
      response => {
        if (response.status === 200) {
          return dispatch(removeDvrSuccess(id))
        } else {
          return dispatch(removeDvrFailure(id, response.reason))
        }
      },
      () =>
        dispatch(
          removeDvrFailure(
            id,
            getP(getState()).t('tv_dvr.error_removing_dvr', {
              _: 'An error occurred while trying to remove the DVR',
            })
          )
        )
    )
}
